<!--
  The lead modal (E2). One screen, three destinations: the person fields -> the primary Contact,
  the document and the company block -> the account, the dates of Qualificação -> the deal (spec A-10).
  MatDialog shell, Hyper markup inside. No tabs and no person/company switch: a CNPJ in the
  document field reveals the company block beside and below it.

  Every section opens with the same header — an `h5` title and a muted one-line hint — so the
  form reads top to bottom as a sequence of steps (owner request 2026-09-15).
-->
<div id="customer-modal" role="dialog" aria-labelledby="customerModalLabel" class="modal fade show" tabindex="-1" aria-modal="true">
  <div class="modal-dialog modal-dialog-centered modal-xl">
    <div class="modal-content">
      <div class="modal-header">
        <h4 class="modal-title" id="customerModalLabel">
          {{ (isNew ? 'kanban.leadModal.titleAdd' : 'kanban.leadModal.titleEdit') | translate }}
        </h4>
        <button type="button" class="btn-close" aria-hidden="true" (click)="close()"></button>
      </div>

      <div class="modal-body scroll">
        <!-- ===================== Contato principal ===================== -->
        <form [formGroup]="contactForm">
          <h5 class="mb-1">{{ 'kanban.leadModal.primaryContact' | translate }}</h5>
          <p class="text-muted font-13 mb-3">
            {{ (isNew ? 'kanban.leadModal.primaryContactHintNew' : 'kanban.leadModal.primaryContactHint') | translate }}
          </p>

          <div class="row">
            <div class="col-md-7 mb-3">
              <label class="form-label">{{ 'kanban.customerFormModal.name' | translate }} <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="name" cdkFocusInitial [placeholder]="'kanban.customerFormModal.namePlaceholder' | translate" />
              <field-error-component [field]="'name'" [label]="'kanban.customerFormModal.name' | translate" [formGroup]="contactForm" />
            </div>
            <div class="col-md-5 mb-3">
              <label class="form-label">{{ 'kanban.leadModal.position' | translate }}</label>
              <input type="text" class="form-control" formControlName="contactPosition" [placeholder]="'kanban.leadModal.positionPlaceholder' | translate" />
            </div>
          </div>
          <div class="row">
            <div class="col-lg-2 mb-3">
              <label class="form-label">{{ 'kanban.customerFormModal.country' | translate }} <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="country" (change)="onCountryChange($event)">
                <option [value]="null">{{ 'kanban.customerFormModal.select' | translate }}</option>
                @for (country of countries; track $index) {
                  <option [value]="country.code">{{ selectedCountryCode === country.code ? country.flag + ' ' + country.code : country.name }}</option>
                }
              </select>
              <field-error-component [field]="'country'" [label]="'kanban.customerFormModal.country' | translate" [formGroup]="contactForm" />
            </div>
            <div class="col-lg-5 mb-3">
              <label class="form-label">{{ 'kanban.customerFormModal.phone' | translate }} <span class="text-danger">*</span></label>
              <input class="form-control" maxlength="25" formControlName="phone" placeholder="000000000" [mask]="phoneMask" (keypress)="keyPressOnlyNumbers($event)" />
              <field-error-component [field]="'phone'" [label]="'kanban.customerFormModal.phone' | translate" [formGroup]="contactForm" />
            </div>
            <div class="col-lg-5 mb-3">
              <label class="form-label">{{ 'kanban.customerFormModal.email' | translate }}</label>
              <input type="text" class="form-control" formControlName="email" [placeholder]="'kanban.customerFormModal.emailPlaceholder' | translate" />
            </div>
          </div>

          <!-- Employer and location of the person: the score reads them and no other CRM screen offers them. -->
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">{{ 'kanban.leadModal.contactCompany' | translate }}</label>
              <input type="text" class="form-control" formControlName="contactCompany" [placeholder]="'kanban.leadModal.contactCompanyPlaceholder' | translate" />
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label">{{ 'kanban.leadModal.city' | translate }}</label>
              <input type="text" class="form-control" formControlName="city" />
            </div>
            <div class="col-md-2 mb-3">
              <label class="form-label">{{ 'kanban.leadModal.state' | translate }}</label>
              <input type="text" class="form-control" maxlength="2" formControlName="state" />
            </div>
          </div>

          <!-- Creation: the person picked as primary (the first pick of "Selecionar existente"). -->
          @if (isNew && selectedContact) {
            <div class="d-flex align-items-center gap-2 border rounded p-2 mb-3">
              <img class="rounded-circle avatar-xs" [alt]="selectedContact.name" [src]="getContactImage(selectedContact)" (error)="onImageError($event)" />
              <strong class="text-truncate">{{ selectedContact.name }}</strong>
              <span class="text-muted">{{ getMaskedPhone(selectedContact.phone) }}</span>
              <span class="badge bg-soft-primary text-primary font-11">{{ 'kanban.leadModal.primaryContact' | translate }}</span>
              <button type="button" class="btn btn-sm btn-soft-danger ms-auto" (click)="removeSelectedContact()">
                <i class="ri-close-line"></i> {{ 'kanban.customerFormModal.remove' | translate }}
              </button>
            </div>
          }
        </form>

        <!-- ===================== Documento (+ empresa quando CNPJ) ===================== -->
        <hr />
        <form [formGroup]="accountForm">
          <h5 class="mb-1">{{ 'kanban.leadModal.documentSection' | translate }}</h5>
          <p class="text-muted font-13 mb-3">{{ 'kanban.leadModal.documentHint' | translate }}</p>

          <div class="row">
            <div class="col-md-4 mb-3">
              <label class="form-label">
                {{ 'kanban.leadModal.document' | translate }}
                <!-- Which document the digits form so far. Not a kind of customer: the account has none. -->
                @if (documentLabel) {
                  <span class="badge bg-soft-secondary text-secondary font-11 ms-1">{{ documentLabel }}</span>
                }
              </label>
              <input type="text" class="form-control" formControlName="document" [mask]="documentMask" />
            </div>
            <!-- The customer's code in the company's ERP, when there is one — an external identifier, kept as given. -->
            <div class="col-md-4 mb-3">
              <label class="form-label">{{ 'kanban.leadModal.code' | translate }}</label>
              <input type="text" class="form-control" formControlName="code" />
              <small class="text-muted">{{ 'kanban.leadModal.codeHint' | translate }}</small>
            </div>
            @if (hasCnpj) {
              <div class="col-md-4 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.tradeName' | translate }}</label>
                <input type="text" class="form-control" formControlName="tradeName" />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.legalName' | translate }}</label>
                <input type="text" class="form-control" formControlName="legalName" />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.stateRegistration' | translate }}</label>
                <input type="text" class="form-control" formControlName="stateRegistration" />
                @if (accountForm.get('stateRegistration')?.touched && accountForm.get('stateRegistration')?.invalid) {
                  <span class="help-block text-danger"><small>{{ 'kanban.leadModal.stateRegistrationInvalid' | translate }}</small></span>
                } @else {
                  <small class="text-muted">{{ 'kanban.leadModal.stateRegistrationHint' | translate }}</small>
                }
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.website' | translate }}</label>
                <input type="text" class="form-control" formControlName="website" />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.instagram' | translate }}</label>
                <input type="text" class="form-control" formControlName="instagram" placeholder="@" />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.segment' | translate }}</label>
                <select class="form-select" formControlName="segment">
                  <option value="">{{ 'kanban.leadModal.selectOption' | translate }}</option>
                  @for (segment of lists.segments; track segment) {
                    <option [value]="segment">{{ segment }}</option>
                  }
                </select>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.employeeBand' | translate }}</label>
                <select class="form-select" formControlName="employeeBand">
                  <option value="">{{ 'kanban.leadModal.selectOption' | translate }}</option>
                  @for (band of lists.employeeBands; track band) {
                    <option [value]="band">{{ band }}</option>
                  }
                </select>
              </div>
            }
          </div>

          @if (hasCnpj) {
            <h5 class="mt-2 mb-2">{{ 'kanban.leadModal.addressTitle' | translate }}</h5>
            <div class="row" formGroupName="address">
              <div class="col-md-3 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.zip' | translate }}</label>
                <input type="text" class="form-control" formControlName="zip" [mask]="zipMask" (blur)="onZipBlur()" />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.street' | translate }}</label>
                <input type="text" class="form-control" formControlName="street" />
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.number' | translate }}</label>
                <input type="text" class="form-control" formControlName="number" />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.complement' | translate }}</label>
                <input type="text" class="form-control" formControlName="complement" />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.neighborhood' | translate }}</label>
                <input type="text" class="form-control" formControlName="neighborhood" />
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.city' | translate }}</label>
                <input type="text" class="form-control" formControlName="city" />
              </div>
              <div class="col-md-1 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.state' | translate }}</label>
                <input type="text" class="form-control" maxlength="2" formControlName="state" />
              </div>
            </div>
          }
        </form>

        <!-- ===================== Contatos vinculados ===================== -->
        <!-- Linked on the spot when editing; queued and sent together with the account when creating. -->
        <hr />
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">
            {{ 'kanban.leadModal.additionalContacts' | translate }}
            @if (additionalContacts.length > 0) {
              <span class="badge bg-soft-secondary text-secondary rounded-pill ms-1">{{ additionalContacts.length }}</span>
            }
          </h5>
          @if (!addingContact && !pickingContact) {
            <div class="d-flex gap-1">
              <button type="button" class="btn btn-sm btn-soft-primary" (click)="pickingContact = true">
                <i class="ri-user-search-line me-1"></i>{{ 'kanban.leadModal.addExisting' | translate }}
              </button>
              <button type="button" class="btn btn-sm btn-soft-primary" (click)="addingContact = true">
                <i class="ri-user-add-line me-1"></i>{{ 'kanban.leadModal.addNew' | translate }}
              </button>
            </div>
          }
        </div>
        <p class="text-muted font-13 mb-3">
          {{ (isNew ? 'kanban.leadModal.additionalContactsHintNew' : 'kanban.leadModal.additionalContactsHint') | translate }}
        </p>

        @if (pickingContact) {
          <div class="border rounded p-3 mb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-muted font-13">{{ 'kanban.leadModal.pickLinkedHint' | translate }}</span>
              <button type="button" class="btn btn-sm btn-light" (click)="closeContactForm()">
                {{ 'kanban.customerFormModal.cancel' | translate }}
              </button>
            </div>
            <contact-list-component (onSelect)="onLinkedContactSelected($event)" />
          </div>
        }

        @if (addingContact) {
          <form [formGroup]="newContactForm" class="border rounded p-3 mb-3">
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label">{{ 'kanban.customerFormModal.name' | translate }} <span class="text-danger">*</span></label>
                <input type="text" class="form-control" formControlName="name" />
                <field-error-component [field]="'name'" [label]="'kanban.customerFormModal.name' | translate" [formGroup]="newContactForm" />
              </div>
              <div class="col-md-2 mb-3">
                <label class="form-label">{{ 'kanban.customerFormModal.country' | translate }} <span class="text-danger">*</span></label>
                <select class="form-select" formControlName="country" (change)="onCountryChange($event, 'new')">
                  <option [value]="null">{{ 'kanban.customerFormModal.select' | translate }}</option>
                  @for (country of countries; track $index) {
                    <option [value]="country.code">{{ country.name }}</option>
                  }
                </select>
                <field-error-component [field]="'country'" [label]="'kanban.customerFormModal.country' | translate" [formGroup]="newContactForm" />
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">{{ 'kanban.customerFormModal.phone' | translate }} <span class="text-danger">*</span></label>
                <input class="form-control" maxlength="25" formControlName="phone" [mask]="newContactPhoneMask" (keypress)="keyPressOnlyNumbers($event)" />
                <field-error-component [field]="'phone'" [label]="'kanban.customerFormModal.phone' | translate" [formGroup]="newContactForm" />
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.role' | translate }}</label>
                <select class="form-select" formControlName="role">
                  @for (role of roles; track role) {
                    <option [value]="role">{{ roleLabel(role) | translate }}</option>
                  }
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">{{ 'kanban.customerFormModal.email' | translate }}</label>
                <input type="text" class="form-control" formControlName="email" />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">{{ 'kanban.leadModal.position' | translate }}</label>
                <input type="text" class="form-control" formControlName="contactPosition" />
              </div>
            </div>
            <div class="d-flex justify-content-end gap-2">
              <button type="button" class="btn btn-light" (click)="closeContactForm()" [disabled]="savingContact">
                {{ 'kanban.customerFormModal.cancel' | translate }}
              </button>
              <button type="button" class="btn btn-primary" (click)="addContact()" [disabled]="savingContact">
                @if (savingContact) {
                  <span class="spinner-border spinner-border-sm me-1"></span>
                }
                {{ 'kanban.leadModal.confirmContact' | translate }}
              </button>
            </div>
          </form>
        }

        @if (additionalContacts.length > 0) {
          <div class="table-responsive">
            <table class="table table-centered table-hover mb-0">
              <thead>
                <tr>
                  <th class="text-primary-emphasis">{{ 'kanban.customerFormModal.colName' | translate }}</th>
                  <th class="text-primary-emphasis">{{ 'kanban.customerFormModal.colPhone' | translate }}</th>
                  <th class="text-primary-emphasis">{{ 'kanban.leadModal.role' | translate }}</th>
                  <th><span class="sr-only"></span></th>
                </tr>
              </thead>
              <tbody>
                @for (entry of additionalContacts; track $index) {
                  <tr>
                    <td>
                      <div class="d-flex flex-column">
                        <span class="fw-bold">{{ entry.contact.name }}</span>
                        <small class="text-muted">{{ entry.contact.contactPosition || entry.contact.email || '' }}</small>
                      </div>
                    </td>
                    <td>{{ getMaskedPhone(entry.contact.phone) }}</td>
                    <td><span class="badge bg-soft-secondary text-secondary">{{ roleLabel(entry.role) | translate }}</span></td>
                    <td class="text-end">
                      <button class="btn btn-dropdown-list" type="button" [matMenuTriggerFor]="contactMenu" (click)="$event.stopPropagation()">
                        <i class="mdi mdi-dots-vertical"></i>
                      </button>
                      <mat-menu #contactMenu="matMenu">
                        @if (!isNew) {
                          <button mat-menu-item type="button" (click)="promoteContact(entry)">{{ 'kanban.leadModal.promote' | translate }}</button>
                        }
                        <button mat-menu-item type="button" (click)="removeContact(entry)">{{ 'kanban.leadModal.removeContact' | translate }}</button>
                      </mat-menu>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }

        <!-- ===================== Qualificação (only when there is something to edit) ===================== -->
        @if (card || qualificationFields.length > 0) {
          <hr />
          <form [formGroup]="accountForm">
            <h5 class="mb-1">{{ 'kanban.leadModal.qualification' | translate }}</h5>
            <p class="text-muted font-13 mb-3">{{ 'kanban.leadModal.qualificationHint' | translate }}</p>
            @if (card) {
              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="form-label">{{ 'kanban.leadModal.owner' | translate }}</label>
                  <input type="text" class="form-control" [value]="card.user?.name ?? '-'" disabled />
                </div>
                <div class="col-md-5 mb-3">
                  <label class="form-label">{{ 'kanban.leadModal.presentedAt' | translate }}</label>
                  <div class="d-flex gap-2">
                    <div class="input-group">
                      <input class="form-control" [matDatepicker]="presentedPicker" formControlName="presentedAt" />
                      <button class="btn btn-light" type="button" (click)="presentedPicker.open()" [attr.aria-label]="'kanban.nextAction.openCalendar' | translate">
                        <i class="ri-calendar-line"></i>
                      </button>
                    </div>
                    <mat-datepicker #presentedPicker></mat-datepicker>
                    <input type="time" class="form-control presented-time" formControlName="presentedTime" />
                  </div>
                  <small class="text-muted">{{ 'kanban.leadModal.presentedAtHint' | translate }}</small>
                </div>
                <div class="col-md-3 mb-3">
                  <label class="form-label">{{ 'kanban.leadModal.includedAt' | translate }}</label>
                  <div class="input-group">
                    <input class="form-control" [matDatepicker]="includedPicker" formControlName="includedAt" />
                    <button class="btn btn-light" type="button" (click)="includedPicker.open()" [attr.aria-label]="'kanban.nextAction.openCalendar' | translate">
                      <i class="ri-calendar-line"></i>
                    </button>
                  </div>
                  <mat-datepicker #includedPicker></mat-datepicker>
                  <small class="text-muted">{{ 'kanban.leadModal.includedAtHint' | translate }}</small>
                </div>
              </div>
            }

            @if (qualificationFields.length > 0) {
              <div class="row" formGroupName="customFields">
                @for (field of qualificationFields; track field._id) {
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ field.name }}</label>
                    @switch (field.type) {
                      @case ('NUMBER') { <input type="number" class="form-control" [formControlName]="field._id" /> }
                      @case ('DATE') { <input type="date" class="form-control" [formControlName]="field._id" /> }
                      @case ('FREETEXT') { <textarea class="form-control" rows="2" [formControlName]="field._id"></textarea> }
                      @default { <input type="text" class="form-control" [formControlName]="field._id" /> }
                    }
                    @if (field.description) { <small class="text-muted">{{ field.description }}</small> }
                  </div>
                }
              </div>
            }
          </form>
        }
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-light" (click)="close()">{{ 'kanban.customerFormModal.cancel' | translate }}</button>
        <button class="btn btn-primary" [disabled]="loading" (click)="save()">
          @if (loading) {
            <mat-spinner [diameter]="25" mode="indeterminate" class="px-3" />
          } @else {
            {{ (isNew ? 'kanban.customerFormModal.save' : 'kanban.customerFormModal.update') | translate }}
          }
        </button>
      </div>
    </div>
  </div>
</div>
